import React from 'react';
import {
  AnimatePresence,
  motion,
  animate,
  useMotionValue,
  useTransform,
} from 'framer-motion';
import styles from './index.module.scss';

interface NpsScoreInputProps {
  className?: string;
  label?: string | null;
  defaultValue?: number | null;
  onChange?: (value: number) => void;
  initialDelay?: number;
}

const NPS_VALUES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// thumbPercent 0 → value 1 (leftmost), 1 → value 10 (rightmost)
// The track spans from 5% to 95% of the container (centres of the 10 equal columns)
function NpsScoreInput({
  className = '',
  label,
  defaultValue = null,
  onChange,
  initialDelay = 0,
}: NpsScoreInputProps) {
  const trackAreaRef = React.useRef<HTMLDivElement>(null);

  // Keep selected value in a ref so pan-end closures are never stale
  const selectedValueRef = React.useRef<number | null>(defaultValue);
  const [selectedValue, setSelectedValue] = React.useState<number | null>(
    defaultValue,
  );
  const [isDragging, setIsDragging] = React.useState(false);

  // Core motion value driving all derived positions
  const thumbPercent = useMotionValue(
    defaultValue !== null ? (defaultValue - 1) / 9 : 0,
  );

  // CSS "left" for the thumb anchor: 5 % … 95 % matching the grid column centres
  const thumbLeft = useTransform(
    thumbPercent,
    (v) => `${(v * 0.9 + 0.05) * 100}%`,
  );

  // Fill width as a fraction of the track line (track line = 90 % of container)
  const fillWidth = useTransform(thumbPercent, (v) => `${v * 100}%`);

  // ─── helpers ────────────────────────────────────────────────────────────────

  const snapToValue = React.useCallback(
    (value: number, commit = true) => {
      animate(thumbPercent, (value - 1) / 9, {
        type: 'spring',
        stiffness: 500,
        damping: 35,
        mass: 0.8,
      });
      setSelectedValue(value);
      selectedValueRef.current = value;
      if (commit && onChange) {
        onChange(value);
      }
    },
    [onChange, thumbPercent],
  );

  const getValueFromClientX = React.useCallback((clientX: number): number => {
    if (!trackAreaRef.current) return 1;
    const rect = trackAreaRef.current.getBoundingClientRect();
    const usableLeft = rect.left + rect.width * 0.05;
    const usableWidth = rect.width * 0.9;
    const fraction = Math.max(
      0,
      Math.min(1, (clientX - usableLeft) / usableWidth),
    );
    return Math.round(fraction * 9 + 1);
  }, []);

  const updatePositionFromClientX = React.useCallback(
    (clientX: number) => {
      if (!trackAreaRef.current) return;
      const rect = trackAreaRef.current.getBoundingClientRect();
      const usableLeft = rect.left + rect.width * 0.05;
      const usableWidth = rect.width * 0.9;
      const fraction = Math.max(
        0,
        Math.min(1, (clientX - usableLeft) / usableWidth),
      );
      thumbPercent.set(fraction);
      const snapped = Math.round(fraction * 9 + 1);
      if (snapped !== selectedValueRef.current) {
        setSelectedValue(snapped);
        selectedValueRef.current = snapped;
      }
    },
    [thumbPercent],
  );

  // ─── render ─────────────────────────────────────────────────────────────────

  return (
    <motion.div
      className={`${styles.container} ${className}`}
      initial={{ opacity: 0, translateY: '30px' }}
      animate={{ opacity: 1, translateY: '0%' }}
      exit={{ opacity: 0, transition: { delay: 0 } }}
      transition={{ duration: 0.45, delay: initialDelay, ease: 'anticipate' }}
    >
      {label && <div className={styles.label}>{label}</div>}

      <div className={styles.sliderWrapper}>
        {/* ── Track area (full-width hit target for drag / click) ── */}
        <motion.div
          className={styles.trackArea}
          ref={trackAreaRef}
          onPanStart={(_, info) => {
            setIsDragging(true);
            updatePositionFromClientX(info.point.x);
          }}
          onPan={(_, info) => {
            updatePositionFromClientX(info.point.x);
          }}
          onPanEnd={() => {
            setIsDragging(false);
            const val = selectedValueRef.current;
            if (val !== null) {
              snapToValue(val, true);
            }
          }}
          onClick={(e) => {
            snapToValue(getValueFromClientX(e.clientX), true);
          }}
        >
          {/* Visual track line (5 % → 95 % of trackArea) */}
          <div className={styles.trackLine}>
            <motion.div className={styles.fill} style={{ width: fillWidth }} />
          </div>

          {/* Tick marks – one per value, pinned inside the trackLine */}
          {NPS_VALUES.map((v, i) => (
            <div
              key={v}
              className={`${styles.tick} ${
                selectedValue !== null && v <= selectedValue
                  ? styles.tickActive
                  : ''
              }`}
              style={{ left: `${(i / 9) * 0.9 * 100 + 5}%` }}
            />
          ))}

          {/* Thumb – absolute within trackArea, centred via negative margins in CSS */}
          <motion.div
            className={`${styles.thumb} ${
              isDragging ? styles.thumbDragging : ''
            } ${selectedValue === null ? styles.thumbHidden : ''}`}
            style={{ left: thumbLeft }}
            animate={{ scale: isDragging ? 1.18 : 1 }}
            transition={{ type: 'spring', stiffness: 600, damping: 25 }}
          >
            <AnimatePresence mode="wait">
              {selectedValue !== null ? (
                <motion.span
                  key={'dot-value'}
                  className={styles.thumbValue}
                  initial={{ scale: 0.4, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.4, opacity: 0 }}
                  transition={{
                    type: 'spring',
                    stiffness: 600,
                    damping: 25,
                    duration: 0.12,
                  }}
                >
                  {selectedValue}
                </motion.span>
              ) : (
                <motion.div
                  key="dot"
                  className={styles.thumbDot}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                />
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>

        {/* ── Number buttons ── */}
        <div className={styles.numbersRow}>
          {NPS_VALUES.map((v) => (
            <button
              key={v}
              type="button"
              className={`${styles.numberBtn} ${
                selectedValue === v ? styles.numberBtnActive : ''
              } ${
                selectedValue !== null && v < selectedValue
                  ? styles.numberBtnPast
                  : ''
              }`}
              onClick={(e) => {
                e.stopPropagation();
                snapToValue(v, true);
              }}
            >
              {v}
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export default NpsScoreInput;
